import React, { useState, useEffect } from 'react'
import { useNavigate, Link, useLocation } from 'react-router-dom'
import { supabase } from '../utils/supabase'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import { SessionRecoveryBanner } from '../components/auth/SessionRecoveryBanner'
import { AlertCircle, HelpCircle } from 'lucide-react'
import { useUt } from '../context/UtFlagContext'
import { UtButton } from '../components/ut'
import { CardFan, UtPublicLinks, useTopXpCards } from '../components/ut/auth/UtAuthFrame'
import { AuthHeading, AuthInput, AuthNotice, OrRule, PasswordInput, SideLink, UtAuthCard, UtCheckEmail } from '../components/ut/auth/AuthParts'

const Login: React.FC = () => {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [showDiagnostics, setShowDiagnostics] = useState(false)
  // UT shows sign-in problems inline instead of as toasts.
  const [loginIssue, setLoginIssue] = useState<null | 'bad' | 'unverified' | 'resend-failed'>(null)
  const [magicError, setMagicError] = useState<string | null>(null)
  const [magicSentTo, setMagicSentTo] = useState<string | null>(null)
  const navigate = useNavigate()
  const location = useLocation()
  const auth = useAuth()
  const { enabled: ut } = useUt()
  const topCards = useTopXpCards(3)

  // Get the redirect path from location state OR query parameter
  // Query param: /login?redirect=/results/79
  // State: navigate('/login', { state: { from: '/results/79' } })
  const searchParams = new URLSearchParams(location.search)
  const redirectParam = searchParams.get('redirect')
  const stateFrom = (location.state as { from?: string })?.from
  const from = redirectParam || stateFrom || '/'

  // Track specific problem users (you can update these)
  const problemUserEmails = [
    // Add the emails of the two problematic users here (redacted for privacy)
    'toffeetower@hotmail.com',
    // Add the second problematic user email here
  ]

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsLoading(true)
    setLoginIssue(null)
    setMagicError(null)

    try {
      // Clear any existing session issues before attempting login
      if (auth.sessionHealth === 'unhealthy') {
        console.log('Clearing unhealthy session before login attempt')
        await auth.signOut()
      }

      const { error } = await auth.signIn(email, password)
      
      if (error) {
        // Enhanced error handling for specific issues
        const errorMessage = error.message?.toLowerCase() || ''

        if (errorMessage.includes('email not confirmed')) {
          // Get new confirmation email
          const { error: resendError } = await supabase.auth.resend({
            type: 'signup',
            email: email
          })

          setLoginIssue(resendError ? 'resend-failed' : 'unverified')
          if (!ut) {
            if (resendError) {
              toast.error('Failed to resend verification email. Please try again.')
            } else {
              toast.error('Please verify your email address. A new verification email has been sent.')
            }
          }
        } else if (errorMessage.includes('invalid login credentials') || errorMessage.includes('incorrect')) {
          // Special handling for recurring login issues
          const isProblemUser = problemUserEmails.includes(email.toLowerCase())

          if (isProblemUser) {
            toast.error(
              <div>
                <p className="font-bold">Login Failed</p>
                <p className="text-sm mt-1">We're aware of ongoing issues with your account.</p>
                <p className="text-sm mt-1">Please try:</p>
                <ul className="text-xs mt-1 list-disc list-inside">
                  <li>Using the "Forgot Password" option below</li>
                  <li>Requesting a magic link from support</li>
                  <li>Clearing your browser cache and cookies</li>
                </ul>
              </div>,
              { duration: 8000 }
            )
            setShowDiagnostics(true)
          } else {
            setLoginIssue('bad')
            if (!ut) toast.error('Invalid email or password. Please try again.')
          }
        } else if (errorMessage.includes('refresh_token') || errorMessage.includes('jwt')) {
          toast.error('Session expired. Please try logging in again.')

          // Attempt automatic recovery
          if (auth.recoverSession) {
            const recovered = await auth.recoverSession()
            if (recovered) {
              toast.success('Session recovered! Please try again.')
            }
          }
        } else {
          throw error
        }
        return
      }

      toast.success('Logged in successfully!')
      navigate(from, { replace: true })
    } catch (error: any) {
      console.error('Login error:', error)
      toast.error(error.message || 'Login failed. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  // Emails a one-time sign-in link to an existing account. Returns an error message, or null.
  const requestMagicLink = async (to: string): Promise<string | null> => {
    const { error } = await supabase.auth.signInWithOtp({
      email: to,
      options: {
        shouldCreateUser: false
      }
    })
    return error ? error.message || 'Could not send the sign-in link.' : null
  }

  // Function to send magic link (backup method)
  const sendMagicLink = async () => {
    if (!email) {
      toast.error('Please enter your email address first')
      return
    }

    setIsLoading(true)
    try {
      const message = await requestMagicLink(email)
      if (message) throw new Error(message)

      toast.success('Magic link sent! Check your email.')
    } catch (error: any) {
      toast.error('Failed to send magic link: ' + error.message)
    } finally {
      setIsLoading(false)
    }
  }

  // UT: "Email me a sign-in link" goes to the Check your email screen.
  const sendUtMagicLink = async () => {
    setLoginIssue(null)
    if (!email) {
      setMagicError('Enter your email first, then we can send you a link.')
      return
    }
    setIsLoading(true)
    setMagicError(null)
    const message = await requestMagicLink(email)
    setIsLoading(false)
    if (message) setMagicError(message)
    else setMagicSentTo(email)
  }

  if (ut) {
    if (magicSentTo) {
      return <UtCheckEmail email={magicSentTo} kind="magic" onResend={() => requestMagicLink(magicSentTo)} onBack={() => setMagicSentTo(null)} />
    }
    return (
      <section className="grid grid-cols-[repeat(auto-fit,minmax(min(100%,340px),1fr))] items-center gap-7 md:min-h-[640px]">
        <div className="hidden md:block">
          <CardFan cards={topCards} />
        </div>
        <UtAuthCard>
          {auth && auth.sessionHealth && (
            <div className="ut-classic">
              <SessionRecoveryBanner
                sessionHealth={auth.sessionHealth}
                isRecovering={auth.isRecovering || false}
                onRecoverSession={auth.recoverSession || (() => Promise.resolve(false))}
                error={auth.error}
              />
            </div>
          )}
          <AuthHeading eyebrow="WEDNESDAY NIGHT FOOTBALL" title="Sign in" size="lg" />
          {loginIssue === 'bad' && <AuthNotice tone="bad">That email and password don&apos;t match. Try again or use a sign-in link.</AuthNotice>}
          {loginIssue === 'unverified' && (
            <AuthNotice tone="acc">
              <b>Verify your email first.</b> We&apos;ve sent a new link to {email}.
            </AuthNotice>
          )}
          {loginIssue === 'resend-failed' && (
            <AuthNotice tone="bad">
              <b>Verify your email first.</b> We couldn&apos;t send a new link just now, so try again in a minute.
            </AuthNotice>
          )}
          {magicError && <AuthNotice tone="bad">{magicError}</AuthNotice>}
          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <AuthInput label="Email" type="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
            <PasswordInput
              label="Password"
              side={<SideLink to="/forgot-password">Forgot?</SideLink>}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              invalid={loginIssue === 'bad'}
              required
            />
            <UtButton type="submit" size="lg" block disabled={isLoading || auth?.isRecovering}>
              {isLoading ? 'SIGNING IN…' : 'SIGN IN'}
            </UtButton>
          </form>
          <OrRule />
          <button
            type="button"
            onClick={sendUtMagicLink}
            disabled={isLoading}
            className="rounded-[11px] border border-ut-line p-[13px] text-center text-[15px] font-semibold text-ut-ink transition hover:bg-ut-card2 disabled:opacity-50"
          >
            Email me a sign-in link
          </button>
          <span className="text-center text-sm text-ut-mut">
            New to WNF?{' '}
            <Link to="/register" className="text-ut-acc hover:text-ut-ink">
              Create an account
            </Link>
          </span>
          <UtPublicLinks />
          {showDiagnostics && auth?.getSessionDiagnostics && (
            <details className="rounded-lg bg-ut-card p-2 text-xs">
              <summary className="cursor-pointer font-semibold">Session diagnostics</summary>
              <pre className="mt-2 overflow-x-auto">{JSON.stringify(auth.getSessionDiagnostics(), null, 2)}</pre>
            </details>
          )}
        </UtAuthCard>
      </section>
    )
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
      <div className="px-8 py-6 mt-4 text-left bg-white dark:bg-gray-800 shadow-lg rounded-lg max-w-md w-full">
        {/* Session Recovery Banner */}
        {auth && auth.sessionHealth && (
          <SessionRecoveryBanner
            sessionHealth={auth.sessionHealth}
            isRecovering={auth.isRecovering || false}
            onRecoverSession={auth.recoverSession || (() => Promise.resolve(false))}
            error={auth.error}
          />
        )}

        <h3 className="text-2xl font-bold text-center">Login to your account</h3>
        <form onSubmit={handleSubmit}>
          <div className="mt-4">
            <div>
              <label className="block" htmlFor="email">Email</label>
              <input
                type="email"
                placeholder="Email"
                className="w-full px-4 py-2 mt-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className="mt-4">
              <label className="block">Password</label>
              <input
                type="password"
                placeholder="Password"
                className="w-full px-4 py-2 mt-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-600"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-2">
              <button
                className="btn btn-primary w-full mt-4"
                type="submit"
                disabled={isLoading || auth?.isRecovering}
              >
                {isLoading ? (
                  <span className="loading loading-spinner"></span>
                ) : (
                  'Login'
                )}
              </button>

              {/* Magic Link Option for Problem Users */}
              {showDiagnostics && (
                <div className="mt-2 p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <HelpCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                    <span className="text-sm font-semibold text-blue-900 dark:text-blue-100">Alternative Login Method</span>
                  </div>
                  <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                    If you continue to have issues, you can request a magic link.
                  </p>
                  <button
                    type="button"
                    onClick={sendMagicLink}
                    className="btn btn-sm btn-outline btn-info mt-2 w-full"
                    disabled={isLoading || !email}
                  >
                    Send Magic Link to Email
                  </button>
                </div>
              )}
              <div className="text-sm text-center mt-4">
                <Link to="/forgot-password" className="link link-primary">Forgot password?</Link>
              </div>
              <div className="divider">OR</div>
              <div className="text-sm text-center">
                Don't have an account?{' '}
                <Link to="/register" className="link link-primary">
                  Register here
                </Link>
              </div>
            </div>
          </div>
        </form>

        {/* Session Diagnostics (hidden by default, shown for debugging) */}
        {showDiagnostics && auth?.getSessionDiagnostics && (
          <details className="mt-4 p-2 bg-gray-50 dark:bg-gray-900 rounded text-xs">
            <summary className="cursor-pointer font-semibold">Session Diagnostics</summary>
            <pre className="mt-2 overflow-x-auto">
              {JSON.stringify(auth.getSessionDiagnostics(), null, 2)}
            </pre>
          </details>
        )}
      </div>
    </div>
  )
}

export default Login